import { useCallback, useEffect, useId, useLayoutEffect, useRef } from 'react';

interface UseChatScrollOptions {
  fetchNextPage?: (() => void) | undefined;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  isLoading?: boolean;
  isStreaming?: boolean;
  messages: Array<unknown>;
  scrollDetectionThreshold?: number;
  scrollThreshold?: number;
}

/**
 * Custom hook for managing chat scroll behavior
 * Handles auto-scrolling, scroll position preservation, and infinite scroll detection
 */
export function useChatScroll({
  fetchNextPage,
  hasNextPage = false,
  isFetchingNextPage = false,
  isLoading = false,
  isStreaming = false,
  messages,
  scrollDetectionThreshold = 100,
  scrollThreshold = 150,
}: UseChatScrollOptions) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const spacerRef = useRef<HTMLDivElement>(null);
  const hasInitialScrolled = useRef(false);
  const isFetchingOlderMessagesRef = useRef(false);
  const scrollPositionBeforeFetchRef = useRef(0);
  const scrollHeightBeforeFetchRef = useRef(0);

  const reactId = useId();
  const scrollContainerId = `chat-messages-scroll-${reactId}`;

  // Assign unique ID for InfiniteScroll target
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.id = scrollContainerId;
    }
  }, [scrollContainerId]);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'auto') => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior, block: 'end' });
    }
  }, []);

  // Preserve scroll position when fetching older messages
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    if (isFetchingNextPage) {
      isFetchingOlderMessagesRef.current = true;
      scrollPositionBeforeFetchRef.current = container.scrollTop;
      scrollHeightBeforeFetchRef.current = container.scrollHeight;
      return;
    }

    if (isFetchingOlderMessagesRef.current) {
      setTimeout(() => {
        const previousScrollTop = scrollPositionBeforeFetchRef.current;
        const previousScrollHeight = scrollHeightBeforeFetchRef.current;
        const currentScrollHeight = container.scrollHeight;
        const heightDiff = currentScrollHeight - previousScrollHeight;

        container.scrollTop = previousScrollTop + heightDiff;
        isFetchingOlderMessagesRef.current = false;
      }, 100);
    }
  }, [isFetchingNextPage]);

  // ChatGPT-style anchor via a dynamic spacer.
  //
  // While a response is in flight, we keep a hidden spacer at the bottom of
  // the scroll container whose height = clientHeight − heightOfContentAfterLastUserMsg.
  // With that spacer in place, scrollToBottom resolves scrollTop to exactly the
  // last user message's offset — i.e., the user message ends up flush against
  // the top of the viewport, with the streaming response + thinking banner
  // filling the rest. Once the response grows past the viewport the spacer
  // collapses to 0, and the user message scrolls off the top organically as
  // we keep following the latest tokens.
  const prevIsLoadingRef = useRef(false);
  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    const spacer = spacerRef.current;
    if (!container || !spacer) {
      return;
    }

    const justSubmitted = isLoading && !prevIsLoadingRef.current;
    prevIsLoadingRef.current = isLoading;
    const inFlight = isLoading || isStreaming;

    if (inFlight) {
      const userMessages = container.querySelectorAll<HTMLElement>('[data-role="USER"]');
      const lastUserMessage = userMessages.item(userMessages.length - 1);
      if (lastUserMessage) {
        const containerRect = container.getBoundingClientRect();
        const userMsgRect = lastUserMessage.getBoundingClientRect();
        const userMsgTopInScroll = container.scrollTop + (userMsgRect.top - containerRect.top);
        const currentSpacerHeight = spacer.offsetHeight;
        const realScrollHeight = container.scrollHeight - currentSpacerHeight;
        const contentAfterUserMsg = realScrollHeight - userMsgTopInScroll;
        const newSpacerHeight = Math.max(0, container.clientHeight - contentAfterUserMsg);
        if (Math.abs(newSpacerHeight - currentSpacerHeight) > 1) {
          spacer.style.height = `${newSpacerHeight}px`;
        }
      }
    }

    if (justSubmitted) {
      // Spacer is now sized so that scrollToBottom anchors the new user
      // message at the top of the viewport.
      container.scrollTop = container.scrollHeight;
      hasInitialScrolled.current = true;
      return;
    }

    if (inFlight) {
      // Stay anchored while the user hasn't scrolled away from the bottom.
      const { clientHeight, scrollHeight, scrollTop } = container;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < scrollThreshold;
      if (isNearBottom) {
        container.scrollTop = scrollHeight;
      }
    }
  }, [messages, isLoading, isStreaming, scrollThreshold]);

  // Initial scroll on mount. Reset the latch when the conversation empties so
  // a fresh "new chat" scrolls to the bottom on its first message.
  useEffect(() => {
    if (messages.length === 0) {
      hasInitialScrolled.current = false;
      return undefined;
    }
    if (!hasInitialScrolled.current) {
      const timer = setTimeout(() => {
        scrollToBottom('auto');
        hasInitialScrolled.current = true;
      }, 100);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [messages.length, scrollToBottom]);

  // Fallback manual scroll detection for infinite scroll
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !hasNextPage || !fetchNextPage) {
      return;
    }

    const onScroll = () => {
      if (container.scrollTop < scrollDetectionThreshold && !isFetchingNextPage) {
        fetchNextPage();
      }
    };

    container.addEventListener('scroll', onScroll, { passive: true });
    return () => container.removeEventListener('scroll', onScroll);
  }, [hasNextPage, fetchNextPage, isFetchingNextPage, scrollDetectionThreshold]);

  return {
    messagesEndRef,
    scrollContainerId,
    scrollContainerRef,
    scrollToBottom,
    spacerRef,
  };
}
